import { useState, useEffect, useCallback } from 'react';
import {
  Calculator,
  PiggyBank,
  Wallet,
  RefreshCw,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Settings,
  Play,
  ChevronDown,
  ChevronUp,
  Info,
  Calendar,
  ArrowDownCircle,
  ArrowUpCircle,
  Eye,
  Globe,
  List,
  Table2,
  CheckSquare,
  Search,
  MapPin,
  Edit3,
  Copy,
  ArrowUp,
  ArrowDown,
  TrendingUp,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { DashboardLayout } from '../components/DashboardLayout';
import { brosTaxReservesAPI } from '../lib/api';
import { BROSVatMonths } from '../components/BROSVatMonths';
import { BROSTaxOverview } from '../components/tax/BROSTaxOverview';
import { PayrollTaxDebtsPanel } from '../components/PayrollTaxDebtsPanel';
import { BROSCashWaterfall } from '../components/BROSCashWaterfall';
import { BROSMoneySplitSummary } from '../components/BROSMoneySplitSummary';
import { BROSReserveTransfers } from '../components/BROSReserveTransfers';
import { SupplierPayments } from '../components/SupplierPayments';
import { TaxReserveCountryMonths } from '../components/TaxReserveCountryMonths';
import { BROSMasterLeases } from '../components/BROSMasterLeases';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';
import { formatCurrency } from '../lib/chartFormatters';

function fmtN(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const BUCKET_CONFIG = {
  vat_reserve:      { label: 'VAT Reserve',       icon: Calculator, color: 'text-blue-600',   bg: 'bg-blue-100 dark:bg-blue-900/30' },
  corp_tax_reserve: { label: 'Corp Tax Reserve',   icon: PiggyBank,  color: 'text-purple-600', bg: 'bg-purple-100 dark:bg-purple-900/30' },
  operating_cash:   { label: 'Operating Cash',     icon: Wallet,     color: 'text-green-600',  bg: 'bg-green-100 dark:bg-green-900/30' },
};

// -- Profitability ---------------------------------------------------
//
// Fan, 2026-09-02: "STILL no profitability line". The page showed three cash
// buckets and no answer to "what did we actually earn, and what could we have
// taken out?". operating_cash is not profit - it still contains supplier cost
// and overhead - so this reads the P&L stamped on each accrual instead.

function ProfitabilitySummary({ profitability, note }) {
  const currencies = Object.keys(profitability ?? {}).sort();
  if (currencies.length === 0) return null;

  const ROWS = [
    { key: 'revenue_gross',     label: 'Revenue collected',   sign: 1 },
    { key: 'output_vat',        label: 'less output VAT',     sign: -1 },
    { key: 'supplier_cost',     label: 'less supplier cost',  sign: -1 },
    { key: 'deposit_component', label: 'less refundable deposits', sign: -1 },
    { key: 'profit_before_tax', label: 'Profit before tax',   sign: 1, strong: true },
    { key: 'corp_tax',          label: 'less tax',            sign: -1 },
    { key: 'profit_after_tax',  label: 'Profit after tax (distributable)', sign: 1, strong: true, highlight: true },
  ];

  return (
    <div>
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
        Profitability
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {currencies.map((cur) => {
          const p = profitability[cur];
          const unresolved = Number(p.unresolved_rows ?? 0);
          return (
            <Card key={cur}>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">{cur}</CardTitle>
                  <Badge variant="outline" className="text-xs">
                    {fmtN(p.rows)} payments
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-1">
                {ROWS.map((r) => {
                  const raw = Number(p[r.key] ?? 0);
                  return (
                    <div
                      key={r.key}
                      className={cn(
                        'flex items-baseline justify-between text-sm',
                        r.strong && 'font-semibold border-t pt-1 mt-1',
                        r.highlight && 'text-emerald-600',
                      )}
                    >
                      <span className={cn(!r.strong && 'text-muted-foreground')}>{r.label}</span>
                      <span className="font-mono">
                        {r.sign < 0 ? '-' : ''}{formatCurrency(raw < 0 ? -raw : raw, cur)}
                      </span>
                    </div>
                  );
                })}
                {unresolved > 0 && (
                  <p className="text-xs text-amber-600 pt-2">
                    {fmtN(unresolved)} of {fmtN(p.rows)} payments have no supplier cost on
                    record - their revenue is included but their profit is not, so profit
                    is understated.
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      {note && <p className="text-xs text-muted-foreground mt-2">{note}</p>}
    </div>
  );
}


// ── Country Stat Card ──────────────────────────────────────────────

function CountryStatCard({ stat }) {
  const cur = stat.currency ?? 'GBP';
  const hasCorp = stat.corp_tax_reserve != null && typeof stat.corp_tax_reserve === 'object';
  const corpCurrencies = hasCorp ? Object.keys(stat.corp_tax_reserve).sort() : [];

  const vatOwed = Number(stat.vat_owed ?? stat.vat_reserve?.total ?? 0);
  const outputVat = Number(stat.output_vat ?? stat.vat_reserve?.total ?? 0);
  const inputVat = Number(stat.input_vat ?? 0);
  const hasInputVat = inputVat > 0;
  const isReclaim = vatOwed < 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">{stat.label ?? stat.country}</CardTitle>
          <Badge variant="outline" className="text-xs">{cur}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* VAT owed (net of input VAT) */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">
            {isReclaim ? 'VAT reclaim' : 'VAT due'}{stat.vat_period?.label ? ` (${stat.vat_period.label})` : ''}
            {stat.vat_period?.end ? ` ending ${stat.vat_period.end}` : ''}
          </p>
          <p className={cn("text-lg font-bold font-mono", isReclaim ? "text-emerald-600" : "text-blue-600")}>
            {isReclaim ? '-' : ''}{formatCurrency(Math.abs(vatOwed), cur)}
          </p>
          {/* Output / input breakdown */}
          <div className="flex flex-wrap gap-3 text-xs mt-1">
            <span className="text-muted-foreground">
              Output: <span className="font-mono">{formatCurrency(outputVat, cur)}</span>
            </span>
            {hasInputVat && (
              <span className="text-emerald-600">
                Input: <span className="font-mono">-{formatCurrency(inputVat, cur)}</span>
              </span>
            )}
            {!hasInputVat && (stat.input_vat_unresolved_count ?? 0) > 0 && (
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                Input VAT unresolved
              </Badge>
            )}
          </div>
          {/* Budgeted / transferred split */}
          <div className="flex gap-3 text-xs mt-1">
            <span className="text-muted-foreground">
              Budgeted: <span className="font-mono">{formatCurrency(stat.vat_reserve?.budgeted ?? 0, cur)}</span>
            </span>
            <span className="text-green-600">
              Transferred: <span className="font-mono">{formatCurrency(stat.vat_reserve?.transferred ?? 0, cur)}</span>
            </span>
          </div>
        </div>
        {/* Corp tax: UK only, GBP headline + per-currency breakdown */}
        {hasCorp && (
          <div>
            <p className="text-xs text-muted-foreground mb-1">
              Corp tax (company-wide){stat.financial_year?.label ? ` ${stat.financial_year.label}` : ''}
            </p>
            {/* GBP headline figure */}
            {stat.corp_tax_gbp_headline && (() => {
              const hl = stat.corp_tax_gbp_headline;
              const hlTotal = Number(hl.total ?? 0);
              const hlLoss = hl.loss_carryforward != null ? Number(hl.loss_carryforward) : null;
              return (
                <div className="mb-3">
                  <span className="text-2xl font-bold font-mono text-purple-600">
                    {formatCurrency(hlTotal, 'GBP')}
                  </span>
                  {hlLoss != null && (
                    <span className="text-xs text-amber-600 font-mono ml-2">
                      loss position ({formatCurrency(hlLoss, 'GBP')}, carryforward)
                    </span>
                  )}
                  <div className="flex gap-3 text-xs mt-1">
                    <span className="text-muted-foreground">
                      Budgeted: <span className="font-mono">{formatCurrency(hl.budgeted ?? 0, 'GBP')}</span>
                    </span>
                    <span className="text-green-600">
                      Transferred: <span className="font-mono">{formatCurrency(hl.transferred ?? 0, 'GBP')}</span>
                    </span>
                  </div>
                  {(hl.unconvertible ?? []).length > 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      Excludes {hl.unconvertible.join(', ')} (no FX rate)
                    </p>
                  )}
                </div>
              );
            })()}
            {/* Per-currency native breakdown */}
            {corpCurrencies.map((cc) => {
              const line = stat.corp_tax_reserve[cc];
              const lineTotal = Number(line?.total ?? 0);
              const lossCarry = line?.loss_carryforward != null ? Number(line.loss_carryforward) : null;
              const gbpTotal = line?.gbp_total != null ? Number(line.gbp_total) : null;
              const gbpLoss = line?.gbp_loss_carryforward != null ? Number(line.gbp_loss_carryforward) : null;
              return (
                <div key={cc} className="mb-2 last:mb-0 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{cc}</Badge>
                    <span className="font-mono">
                      {formatCurrency(lineTotal, cc)}
                    </span>
                    {gbpTotal != null && cc !== 'GBP' && (
                      <span className="font-mono text-xs">
                        ({formatCurrency(gbpTotal, 'GBP')})
                      </span>
                    )}
                    {lossCarry != null && (
                      <span className="text-xs text-amber-600 font-mono">
                        loss {formatCurrency(lossCarry, cc)}
                        {gbpLoss != null && cc !== 'GBP' && ` (${formatCurrency(gbpLoss, 'GBP')})`}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-3 text-xs mt-0.5">
                    <span>
                      Budgeted: <span className="font-mono">{formatCurrency(line?.budgeted ?? 0, cc)}</span>
                      {line?.gbp_budgeted != null && cc !== 'GBP' && (
                        <span className="font-mono"> ({formatCurrency(line.gbp_budgeted, 'GBP')})</span>
                      )}
                    </span>
                    <span className="text-green-600">
                      Transferred: <span className="font-mono">{formatCurrency(line?.transferred ?? 0, cc)}</span>
                      {line?.gbp_transferred != null && cc !== 'GBP' && (
                        <span className="font-mono"> ({formatCurrency(line.gbp_transferred, 'GBP')})</span>
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Per-Payment Table ───────────────────────────────────────────────

function PaymentsTable({ payments, totalsByCurrency, statusFilter, onStatusFilterChange, onToggleStatus, onBulkStatus, onRefresh, unresolvedSupplierCount }) {
  const [selected, setSelected] = useState(new Set());
  const [bulking, setBulking] = useState(false);
  const showCount = 100;
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll ? payments : payments.slice(0, showCount);
  const hasDeposits = payments.some((p) => p.deposit_component != null);

  const toggleSelect = (pid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === payments.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(payments.map((p) => p.payment_id)));
    }
  };

  const handleBulkMark = async (status) => {
    if (selected.size === 0) return;
    setBulking(true);
    try {
      await onBulkStatus(Array.from(selected).join(','), status);
      setSelected(new Set());
    } finally {
      setBulking(false);
    }
  };

  if (payments.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No payments found. Run accrual to populate.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base">Per-Payment View</CardTitle>
            <CardDescription>{payments.length} payments{unresolvedSupplierCount > 0 ? ` (${unresolvedSupplierCount} unresolved supplier)` : ''}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
              className="h-8 text-sm border rounded px-2 bg-background"
            >
              <option value="all">All statuses</option>
              <option value="budgeted">Budgeted</option>
              <option value="transferred">Transferred</option>
            </select>
            {/* Bulk actions */}
            {selected.size > 0 && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkMark('transferred')}
                  disabled={bulking}
                >
                  {bulking ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckSquare className="h-3 w-3 mr-1" />}
                  Mark {selected.size} transferred
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleBulkMark('budgeted')}
                  disabled={bulking}
                >
                  Mark budgeted
                </Button>
              </>
            )}
            <Button size="sm" variant="outline" onClick={onRefresh}>
              <RefreshCw className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-8">
                  <input
                    type="checkbox"
                    checked={selected.size === payments.length && payments.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                <th>Invoice</th>
                <th>Date</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Supplier</th>
                {hasDeposits && <th className="text-right">Deposit</th>}
                <th className="text-right">VAT</th>
                <th className="text-right">Corp Tax</th>
                <th className="text-right">Profit</th>
                <th>Ccy</th>
                <th>Country</th>
                <th>Description</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((row) => (
                <tr key={row.payment_id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(row.payment_id)}
                      onChange={() => toggleSelect(row.payment_id)}
                      className="rounded"
                    />
                  </td>
                  <td className="font-mono text-xs">{row.invoice_ref ?? row.payment_id}</td>
                  <td className="text-xs text-muted-foreground whitespace-nowrap">
                    {row.date ? new Date(row.date).toLocaleDateString() : ''}
                  </td>
                  <td className="text-right font-mono text-sm">
                    {formatCurrency(row.amount, row.currency)}
                  </td>
                  <td className="text-right font-mono text-sm">
                    {row.supplier_cost != null
                      ? formatCurrency(row.supplier_cost, row.currency)
                      : <span className="text-muted-foreground italic text-xs">unresolved</span>
                    }
                  </td>
                  {hasDeposits && (
                    <td className="text-right font-mono text-sm text-amber-600">
                      {row.deposit_component != null ? formatCurrency(row.deposit_component, row.currency) : ''}
                    </td>
                  )}
                  <td className="text-right font-mono text-sm text-blue-600">
                    {formatCurrency(row.vat_reserve, row.currency)}
                  </td>
                  <td className="text-right font-mono text-sm text-purple-600">
                    {formatCurrency(row.corp_tax_reserve, row.currency)}
                  </td>
                  <td className="text-right font-mono text-sm">
                    {row.profit != null
                      ? <span className={row.profit < 0 ? 'text-red-600' : 'text-green-600'}>{formatCurrency(row.profit, row.currency)}</span>
                      : <span className="text-muted-foreground italic text-xs">unresolved</span>
                    }
                  </td>
                  <td className="text-xs">{row.currency}</td>
                  <td className="text-xs">{row.country ?? <span className="text-muted-foreground italic">unassigned</span>}</td>
                  <td className="text-xs text-muted-foreground truncate max-w-[120px]">{row.description}</td>
                  <td>
                    <Button
                      size="sm"
                      variant={row.transfer_status === 'transferred' ? 'default' : 'outline'}
                      className={cn(
                        'text-xs h-6 px-2',
                        row.transfer_status === 'transferred'
                          ? 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                          : ''
                      )}
                      onClick={() => onToggleStatus(
                        row.payment_id,
                        row.transfer_status === 'transferred' ? 'budgeted' : 'transferred'
                      )}
                    >
                      {row.transfer_status === 'transferred' ? 'Transferred' : 'Budgeted'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            {totalsByCurrency && Object.keys(totalsByCurrency).length > 0 && (
              <tfoot>
                {Object.entries(totalsByCurrency).map(([cur, t]) => (
                  <tr key={cur} className="font-semibold">
                    <td colSpan={3} className="text-right text-xs">Total ({cur})</td>
                    <td className="text-right font-mono text-sm">{formatCurrency(t.amount, cur)}</td>
                    <td className="text-right font-mono text-sm">{formatCurrency(t.supplier_cost, cur)}</td>
                    {hasDeposits && <td className="text-right font-mono text-sm text-amber-600">{formatCurrency(t.deposit_component, cur)}</td>}
                    <td className="text-right font-mono text-sm text-blue-600">{formatCurrency(t.vat_reserve, cur)}</td>
                    <td className="text-right font-mono text-sm text-purple-600">{formatCurrency(t.corp_tax_reserve, cur)}</td>
                    <td className="text-right font-mono text-sm text-green-600">{formatCurrency(t.profit, cur)}</td>
                    <td colSpan={4} />
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>
        {payments.length > showCount && (
          <div className="p-3 text-center">
            <Button variant="ghost" size="sm" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show less' : `Show all ${payments.length} payments`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Rules Config Card ────────────────────────────────────────────────

function RulesConfigCard({ rules, onSave, saving }) {
  const [editing, setEditing] = useState(false);
  const [corpTaxPct, setCorpTaxPct] = useState(rules?.corp_tax_pct ?? '25');
  const [expenseBudget, setExpenseBudget] = useState(rules?.expense_budget_weekly ?? '0');
  const [profitBuffer, setProfitBuffer] = useState(rules?.profit_buffer ?? '0');

  useEffect(() => {
    setCorpTaxPct(rules?.corp_tax_pct ?? '25');
    setExpenseBudget(rules?.expense_budget_weekly ?? '0');
    setProfitBuffer(rules?.profit_buffer ?? '0');
  }, [rules]);

  const handleSave = async () => {
    await onSave({
      corp_tax_pct: corpTaxPct,
      expense_budget_weekly: expenseBudget,
      profit_buffer: profitBuffer,
    });
    setEditing(false);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Reserve Rules</CardTitle>
          </div>
          {!editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">VAT Handling</p>
            <p className="text-sm font-medium">Exact (from invoice VAT line)</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Corporation Tax Rate</p>
            {editing ? (
              <div className="flex items-center gap-1">
                <Input
                  type="number" min="0" max="100" step="0.1"
                  value={corpTaxPct}
                  onChange={(e) => setCorpTaxPct(e.target.value)}
                  className="w-20 h-8 text-sm"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            ) : (
              <p className="text-sm font-medium font-mono">{rules?.corp_tax_pct ?? '25'}%</p>
            )}
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Weekly Expense Budget</p>
            {editing ? (
              <Input
                type="number" min="0" step="100"
                value={expenseBudget}
                onChange={(e) => setExpenseBudget(e.target.value)}
                className="w-28 h-8 text-sm"
              />
            ) : (
              <p className="text-sm font-medium font-mono">{formatCurrency(rules?.expense_budget_weekly ?? 0)}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Profit Buffer</p>
            {editing ? (
              <div className="flex items-center gap-2">
                <Input
                  type="number" min="0" step="100"
                  value={profitBuffer}
                  onChange={(e) => setProfitBuffer(e.target.value)}
                  className="w-28 h-8 text-sm"
                />
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <p className="text-sm font-medium font-mono">{formatCurrency(rules?.profit_buffer ?? 0)}</p>
            )}
          </div>
        </div>
        {rules?.updated_at && (
          <p className="text-xs text-muted-foreground mt-3">
            Last updated: {new Date(rules.updated_at).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Weekly Proposal Card ─────────────────────────────────────────────

function WeeklyProposalCard({ lastRun, onRunWeekly, running }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Weekly Budget Proposal</CardTitle>
            {lastRun && (
              <Badge variant="outline" className="text-xs">
                Last: {new Date(lastRun.created_at).toLocaleDateString()}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={onRunWeekly} disabled={running} data-testid="run-weekly-btn">
              {running ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Play className="h-3 w-3 mr-1" />}
              Run Now
            </Button>
            {lastRun && (
              <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      {lastRun?.proposals && (
        <CardContent>
          {Object.entries(lastRun.proposals).map(([currency, p]) => (
            <div key={currency} className="mb-4 last:mb-0">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{currency}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Operating Cash</p>
                  <p className="font-mono font-medium">{formatCurrency(p.operating_cash_balance, currency)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Week Additions</p>
                  <p className="font-mono">{formatCurrency(p.operating_cash_additions, currency)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Expense Budget</p>
                  <p className="font-mono">{formatCurrency(p.expense_budget, currency)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Safe Profit Draw</p>
                  <p className={cn(
                    'font-mono font-semibold',
                    p.is_draw_safe ? 'text-green-600' : 'text-red-600',
                  )}>
                    {formatCurrency(p.safe_profit_draw, currency)}
                  </p>
                </div>
              </div>
              {expanded && (
                <div className="mt-2 grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">VAT Reserve</p>
                    <p className="font-mono">{formatCurrency(p.vat_reserve_balance, currency)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Corp Tax Reserve</p>
                    <p className="font-mono">{formatCurrency(p.corp_tax_reserve_balance, currency)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Profit Buffer</p>
                    <p className="font-mono">{formatCurrency(p.profit_buffer, currency)}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
          {lastRun.trigger && (
            <p className="text-xs text-muted-foreground mt-2">
              Trigger: {lastRun.trigger} | {new Date(lastRun.created_at).toLocaleString()}
            </p>
          )}
        </CardContent>
      )}
      {!lastRun && (
        <CardContent>
          <p className="text-sm text-muted-foreground">No weekly run yet. Click "Run Now" to generate a proposal.</p>
        </CardContent>
      )}
    </Card>
  );
}

// ── Sweep / Draw Action Card ─────────────────────────────────────────

function ActionCard({ title, icon: Icon, iconColor, bucketOptions, actionType, onRefresh }) {
  const [bucket, setBucket] = useState(bucketOptions[0]?.value ?? '');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [description, setDescription] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const handlePreview = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error('Enter a positive amount'); return; }
    setLoading(true);
    try {
      const params = { amount, currency, description };
      if (actionType === 'sweep') params.bucket = bucket;
      const res = actionType === 'sweep'
        ? await brosTaxReservesAPI.sweepPreview(params)
        : await brosTaxReservesAPI.drawPreview(params);
      if (res.data.error) { toast.error(res.data.error); return; }
      setPreview(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const params = { amount, currency, description };
      if (actionType === 'sweep') params.bucket = bucket;
      const res = actionType === 'sweep'
        ? await brosTaxReservesAPI.sweepApply(params)
        : await brosTaxReservesAPI.drawApply(params);
      if (res.data.error) { toast.error(res.data.error); return; }
      toast.success(`${actionType === 'sweep' ? 'Sweep' : 'Draw'} applied: ${formatCurrency(amount, currency)}`);
      setPreview(null);
      setAmount('');
      setDescription('');
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Icon className={cn('h-4 w-4', iconColor)} />
          <CardTitle className="text-base">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-3">
          {bucketOptions.length > 1 && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Bucket</p>
              <select
                value={bucket}
                onChange={(e) => { setBucket(e.target.value); setPreview(null); }}
                className="h-8 text-sm border rounded px-2 bg-background"
              >
                {bucketOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Amount</p>
            <Input
              type="number" min="0" step="0.01"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setPreview(null); }}
              className="w-32 h-8 text-sm"
              placeholder="0.00"
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Currency</p>
            <select
              value={currency}
              onChange={(e) => { setCurrency(e.target.value); setPreview(null); }}
              className="h-8 text-sm border rounded px-2 bg-background"
            >
              <option value="GBP">GBP</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
          <div className="flex-1 min-w-[120px]">
            <p className="text-xs text-muted-foreground mb-1">Description</p>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="h-8 text-sm"
              placeholder="e.g. Q1 VAT payment"
            />
          </div>
          <Button size="sm" variant="outline" onClick={handlePreview} disabled={loading}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3 mr-1" />}
            Preview
          </Button>
        </div>

        {preview && (
          <div className="mt-4 p-3 rounded-lg border bg-muted/30">
            <div className="grid grid-cols-3 gap-3 text-sm mb-3">
              <div>
                <p className="text-xs text-muted-foreground">Current Balance</p>
                <p className="font-mono">{formatCurrency(preview.current_balance, currency)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{actionType === 'sweep' ? 'Sweep' : 'Draw'} Amount</p>
                <p className="font-mono">{formatCurrency(preview.amount, currency)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Resulting Balance</p>
                <p className={cn('font-mono font-semibold', preview.would_go_negative ? 'text-red-600' : 'text-green-600')}>
                  {formatCurrency(preview.resulting_balance, currency)}
                </p>
              </div>
            </div>
            {preview.would_go_negative && (
              <div className="flex items-center gap-1 text-xs text-red-600 mb-2">
                <AlertTriangle className="h-3 w-3" /> This will make the bucket negative (over-swept)
              </div>
            )}
            <Button size="sm" onClick={handleApply} disabled={applying}>
              {applying ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
              Apply
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Diagnostics Panel ────────────────────────────────────────────────

function DiagnosticsPanel({ diagnostics, isLoading, onRefresh }) {
  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!diagnostics) return null;

  const { conservation_check, duplicate_accruals, missing_vat_invoices, ledger_reconciliation } = diagnostics;
  const allHealthy = conservation_check?.all_pass && duplicate_accruals?.all_clean;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Diagnostics</CardTitle>
            {allHealthy ? (
              <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                <CheckCircle className="h-3 w-3 mr-1" /> Healthy
              </Badge>
            ) : (
              <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                <AlertTriangle className="h-3 w-3 mr-1" /> Issues
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-3 w-3 mr-1" /> Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary row */}
        <div className="grid grid-cols-4 gap-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">Conservation</p>
            <p className={cn('font-medium', conservation_check?.all_pass ? 'text-green-600' : 'text-red-600')}>
              {conservation_check?.all_pass ? 'Pass' : `${conservation_check?.failures?.length} failures`}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Duplicates</p>
            <p className={cn('font-medium', duplicate_accruals?.all_clean ? 'text-green-600' : 'text-red-600')}>
              {duplicate_accruals?.all_clean ? 'None' : `${duplicate_accruals?.warnings?.length} warnings`}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Missing VAT</p>
            <p className="font-medium">{missing_vat_invoices?.count ?? 0} invoices</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Payments Checked</p>
            <p className="font-medium">{conservation_check?.payments_checked ?? 0}</p>
          </div>
        </div>

        {/* Expanded detail */}
        {expanded && (
          <div className="mt-4 space-y-4">
            {/* Ledger reconciliation */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Ledger Reconciliation
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Total Accrued</p>
                  <p className="font-mono text-sm">{formatCurrency(ledger_reconciliation?.total_accrued)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Swept</p>
                  <p className="font-mono text-sm">{formatCurrency(ledger_reconciliation?.total_swept)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Drawn</p>
                  <p className="font-mono text-sm">{formatCurrency(ledger_reconciliation?.total_drawn)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Net Reserved</p>
                  <p className="font-mono text-sm font-semibold">{formatCurrency(ledger_reconciliation?.net_reserved)}</p>
                </div>
              </div>
              {ledger_reconciliation?.bucket_totals && (
                <div className="mt-2 grid grid-cols-3 gap-3">
                  {Object.entries(ledger_reconciliation.bucket_totals).map(([bucket, total]) => {
                    const cfg = BUCKET_CONFIG[bucket];
                    return (
                      <div key={bucket}>
                        <p className="text-xs text-muted-foreground">{cfg?.label ?? bucket}</p>
                        <p className="font-mono text-sm">{formatCurrency(total)}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Conservation failures */}
            {conservation_check?.failures?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-2">
                  Conservation Failures
                </h4>
                <div className="space-y-1">
                  {conservation_check.failures.map((f) => (
                    <div key={f.payment_id} className="text-xs text-red-700 dark:text-red-400 font-mono">
                      {f.payment_id}: expected {formatCurrency(f.expected)}, got {formatCurrency(f.actual_sum)} (delta {formatCurrency(f.delta)})
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Missing VAT */}
            {missing_vat_invoices?.count > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Paid Invoices with Zero VAT ({missing_vat_invoices.count})
                </h4>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {missing_vat_invoices.invoices.map((inv) => (
                    <div key={inv.bros_id} className="text-xs text-muted-foreground font-mono">
                      {inv.invoice_number} | {inv.company} | total {formatCurrency(inv.total_amount)} | paid {formatCurrency(inv.amount_paid)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Raw Ledger Entries Table (audit view) ─────────────────────────────

function LedgerTable({ entries, showCount = 50 }) {
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll ? entries : entries.slice(0, showCount);

  if (entries.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No ledger entries yet. Run accrual to populate.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Raw Ledger Entries</CardTitle>
        <CardDescription>{entries.length} entries total (3 per payment: VAT, Corp Tax, Operating Cash)</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Payment ID</th>
                <th>Bucket</th>
                <th className="text-right">Amount</th>
                <th>Currency</th>
                <th>Description</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((entry) => {
                const cfg = BUCKET_CONFIG[entry.bucket];
                return (
                  <tr key={entry.id}>
                    <td>
                      <Badge variant="outline" className="text-xs">
                        {entry.type}
                      </Badge>
                    </td>
                    <td className="font-mono text-xs">{entry.payment_id ?? ''}</td>
                    <td>
                      <span className={cn('text-xs font-medium', cfg?.color)}>
                        {cfg?.label ?? entry.bucket}
                      </span>
                    </td>
                    <td className="text-right font-mono text-sm">
                      {formatCurrency(entry.amount, entry.currency)}
                    </td>
                    <td className="text-xs">{entry.currency}</td>
                    <td className="text-xs text-muted-foreground">
                      {entry.description ?? entry.source_snapshot?.company ?? ''}
                    </td>
                    <td className="text-xs text-muted-foreground whitespace-nowrap">
                      {entry.date ? new Date(entry.date).toLocaleDateString() : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {entries.length > showCount && (
          <div className="p-3 text-center">
            <Button variant="ghost" size="sm" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show less' : `Show all ${entries.length} entries`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Recompute Reserves Card ──────────────────────────────────────────

function RecomputeReservesCard({ onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    setApplyResult(null);
    try {
      const { data } = await brosTaxReservesAPI.recomputePreview();
      setPreview(data);
      toast.success('Recompute preview ready');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    if (!preview || preview.rows_changing === 0) return;
    if (!window.confirm(
      `This will delete ${fmtN(preview.old_entry_count)} old accrual entries ` +
      `and rewrite ${fmtN(preview.total_payments)} payments. Continue?`
    )) return;

    setApplying(true);
    try {
      const { data } = await brosTaxReservesAPI.recomputeApply();
      setApplyResult(data);
      setPreview(null);
      toast.success(`Recompute applied: ${fmtN(data.written_entries)} entries written`);
      onRefresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const busy = loading || applying;
  const canApply = preview && preview.rows_changing > 0 && !busy;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Recompute Reserves</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Recompute all accrual entries using the current formula and supplier data.
          Preview shows per-currency deltas; Apply deletes old accruals and writes new ones.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Buttons */}
        <div className="flex items-center gap-2">
          <Button onClick={runPreview} disabled={busy} size="sm" variant="outline">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          <Button
            onClick={runApply}
            disabled={!canApply}
            size="sm"
            variant={canApply ? 'default' : 'outline'}
          >
            {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            {preview ? `Apply ${fmtN(preview.total_payments)} payments` : 'Apply'}
          </Button>
        </div>

        {/* Preview result */}
        {preview && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              PREVIEW (read-only)
            </Badge>

            {/* Per-currency deltas */}
            {Object.entries(preview.currency_deltas).map(([cur, deltas]) => {
              const vatDelta = deltas.vat_reserve ? Number(deltas.vat_reserve.delta) : 0;
              const vatPass = vatDelta === 0;
              return (
                <div key={cur}>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{cur}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {['vat_reserve', 'corp_tax_reserve', 'operating_cash'].map((bucket) => {
                      const d = deltas[bucket];
                      if (!d) return null;
                      const cfg = BUCKET_CONFIG[bucket];
                      const delta = Number(d.delta);
                      return (
                        <div key={bucket} className="border rounded-lg p-2 text-center">
                          <p className="text-xs text-muted-foreground">{cfg?.label ?? bucket} delta</p>
                          <p className={cn(
                            'text-sm font-mono font-semibold',
                            delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-600' : 'text-muted-foreground',
                          )}>
                            {delta >= 0 ? '+' : ''}{formatCurrency(delta, cur)}
                          </p>
                          <p className="text-[10px] text-muted-foreground font-mono">
                            {formatCurrency(d.before, cur)} &rarr; {formatCurrency(d.after, cur)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                  {/* Output VAT delta gate */}
                  <div className={cn(
                    'flex items-center gap-2 mt-1.5 px-2 py-1 rounded text-xs',
                    vatPass
                      ? 'bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400'
                      : 'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400',
                  )}>
                    {vatPass
                      ? <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
                      : <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />}
                    <span>
                      Output VAT delta: <span className="font-mono font-semibold">{formatCurrency(vatDelta, cur)}</span>
                    </span>
                    <Badge className={cn(
                      'text-[9px] ml-auto',
                      vatPass
                        ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                        : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
                    )}>
                      {vatPass ? 'PASS' : 'FAIL'}
                    </Badge>
                  </div>
                </div>
              );
            })}

            {/* Input VAT totals */}
            {preview.input_vat?.totals_by_currency && Object.keys(preview.input_vat.totals_by_currency).length > 0 && (
              <div>
                <p className="text-xs font-medium mb-1">Input VAT totals</p>
                <div className="flex flex-wrap gap-4">
                  {Object.entries(preview.input_vat.totals_by_currency).map(([cur, vals]) => (
                    <div key={cur} className="text-xs space-y-0.5">
                      <p className="font-semibold text-muted-foreground">{cur}</p>
                      <p className="font-mono">Output: {formatCurrency(vals.output_vat, cur)}</p>
                      <p className="font-mono text-emerald-600">Input: -{formatCurrency(vals.input_vat, cur)}</p>
                      <p className="font-mono">Net: {formatCurrency(vals.net_vat_delta, cur)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Deposit excluded summary */}
            {preview.deposit_excluded?.deposit_carrying_payments > 0 && (
              <div>
                <p className="text-xs font-medium mb-1">Deposit excluded from PBT</p>
                <div className="flex flex-wrap gap-4">
                  {Object.entries(preview.deposit_excluded.totals_by_currency).map(([cur, amt]) => (
                    <div key={cur} className="text-xs space-y-0.5">
                      <p className="font-semibold text-muted-foreground">{cur}</p>
                      <p className="font-mono text-amber-600">{formatCurrency(amt, cur)}</p>
                    </div>
                  ))}
                  <div className="text-xs space-y-0.5">
                    <p className="font-semibold text-muted-foreground">Payments</p>
                    <p className="font-mono">{preview.deposit_excluded.deposit_carrying_payments}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Summary metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Rows changing</p>
                <p className={cn(
                  'text-lg font-semibold tabular-nums',
                  preview.rows_changing > 0 ? 'text-amber-600' : 'text-green-600',
                )}>
                  {fmtN(preview.rows_changing)}
                </p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Total payments</p>
                <p className="text-lg font-semibold tabular-nums">{fmtN(preview.total_payments)}</p>
              </div>
              <div className={cn(
                'border rounded-lg p-2 text-center',
                (preview.input_vat?.unresolved_count ?? 0) === 212
                  ? 'border-green-200 dark:border-green-800'
                  : 'border-red-300 dark:border-red-700',
              )}>
                <p className="text-xs text-muted-foreground">Fallback count (exp. 212)</p>
                <p className={cn(
                  'text-lg font-semibold tabular-nums',
                  (preview.input_vat?.unresolved_count ?? 0) === 212 ? 'text-green-600' : 'text-red-600',
                )}>
                  {fmtN(preview.input_vat?.unresolved_count ?? 0)}
                </p>
              </div>
              <div className={cn(
                'border rounded-lg p-2 text-center',
                (preview.sign_mismatch_count ?? 0) > 0
                  ? 'border-red-300 dark:border-red-700'
                  : 'border-green-200 dark:border-green-800',
              )}>
                <p className="text-xs text-muted-foreground">Sign mismatch (must be 0)</p>
                <p className={cn(
                  'text-lg font-semibold tabular-nums',
                  (preview.sign_mismatch_count ?? 0) > 0 ? 'text-red-600' : 'text-green-600',
                )}>
                  {preview.sign_mismatch_count ?? 0}
                </p>
              </div>
            </div>

            <p className="text-[10px] text-muted-foreground">
              Corp tax rate: {preview.corp_tax_pct_used}% | Old entries: {fmtN(preview.old_entry_count)}
            </p>
          </div>
        )}

        {/* Apply result */}
        {applyResult && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
              APPLIED
            </Badge>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Deleted</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtN(applyResult.deleted_entries)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Written</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtN(applyResult.written_entries)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Payments</p>
                <p className="text-lg font-semibold tabular-nums">{fmtN(applyResult.payments_processed)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Batch</p>
                <p className="text-[10px] font-mono text-muted-foreground truncate" title={applyResult.batch_id}>
                  {applyResult.batch_id?.slice(0, 8)}
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Allocation Trace Card ────────────────────────────────────────────

function AllocationTraceCard() {
  const [invoiceInput, setInvoiceInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [trace, setTrace] = useState(null);

  const runTrace = async () => {
    const inv = invoiceInput.trim();
    if (!inv) { toast.error('Enter an invoice number'); return; }
    setLoading(true);
    setTrace(null);
    try {
      const { data } = await brosTaxReservesAPI.getAllocationTrace(inv);
      setTrace(data);
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success('Allocation trace loaded');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const alloc = trace?.allocation;
  const inputVat = trace?.input_vat;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Allocation Trace</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Trace the supplier cost allocation for a client invoice. Shows what the allocator receives and produces.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Invoice Number</p>
            <Input
              value={invoiceInput}
              onChange={(e) => setInvoiceInput(e.target.value)}
              placeholder="e.g. INV-00323"
              className="w-48 h-8 text-sm font-mono"
              onKeyDown={(e) => { if (e.key === 'Enter') runTrace(); }}
            />
          </div>
          <Button size="sm" onClick={runTrace} disabled={loading} data-testid="run-trace-btn">
            {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Search className="h-3 w-3 mr-1" />}
            Run
          </Button>
        </div>

        {trace && !trace.error && (
          <div className="space-y-4">
            {/* Invoice + Booking summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Invoice</p>
                <p className="font-mono font-medium">{trace.invoice_number}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Amount Paid</p>
                <p className="font-mono">{formatCurrency(trace.amount_paid, trace.currency)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Booking</p>
                <p className="font-mono text-xs">{trace.booking_id}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Supplier Total</p>
                <p className="font-mono">{trace.booking?.supplier_total != null ? formatCurrency(trace.booking.supplier_total, trace.currency) : '-'}</p>
              </div>
            </div>

            {/* Allocation result */}
            {alloc && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Allocation Result
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Supplier Gross (this invoice)</p>
                    <p className="font-mono font-semibold text-orange-600" data-testid="trace-supplier-gross">
                      {formatCurrency(alloc.supplier_gross, trace.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Basis Total</p>
                    <p className="font-mono">{formatCurrency(alloc.total_invoiced, trace.currency)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Share Fraction</p>
                    <p className="font-mono">{Number(alloc.share_fraction ?? 0).toFixed(6)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Remainder Absorber</p>
                    <p className="font-mono">{alloc.is_remainder_absorber ? 'Yes' : 'No'}</p>
                  </div>
                </div>
                {/* Deposit fields: basis-side and PBT-side */}
                {(Number(trace.deposit_excluded ?? 0) > 0 || Number(trace.deposit_component ?? 0) > 0) && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-2">
                    <div>
                      <p className="text-xs text-muted-foreground">Deposit Excluded (basis)</p>
                      <p className="font-mono text-amber-600">{formatCurrency(trace.deposit_excluded, trace.currency)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Deposit Component (PBT)</p>
                      <p className="font-mono text-amber-600">{formatCurrency(trace.deposit_component, trace.currency)}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Proration basis table */}
            {alloc?.all_shares?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Proration Basis ({alloc.all_shares.length} invoices)
                </h4>
                <div className="overflow-x-auto">
                  <table className="data-table text-xs">
                    <thead>
                      <tr>
                        <th>Invoice ID</th>
                        <th className="text-right">Amount in Basis</th>
                        <th className="text-right">Allocated Share</th>
                        <th>Remainder</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alloc.all_shares.map((s) => (
                        <tr key={s.invoice_id} className={s.invoice_id === trace.bros_id ? 'bg-blue-50 dark:bg-blue-950/30' : ''}>
                          <td className="font-mono">{s.invoice_id}</td>
                          <td className="text-right font-mono">{formatCurrency(s.amount, trace.currency)}</td>
                          <td className="text-right font-mono">{formatCurrency(s.share, trace.currency)}</td>
                          <td>{s.is_remainder_absorber ? <Badge variant="outline" className="text-[10px]">absorber</Badge> : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Excluded invoices */}
            {trace.excluded_invoices?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-2">
                  Excluded from Basis ({trace.excluded_invoices.length})
                </h4>
                <div className="space-y-1">
                  {trace.excluded_invoices.map((ex, i) => (
                    <div key={i} className="text-xs font-mono text-amber-700 dark:text-amber-400">
                      {ex.invoice_number ?? ex.invoice_id}: {ex.reason} (amount: {formatCurrency(ex.amount_paid, trace.currency)})
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Supplier invoices (input VAT) */}
            {trace.supplier_invoices?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Supplier Invoices (Input VAT)
                </h4>
                <div className="overflow-x-auto">
                  <table className="data-table text-xs">
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th className="text-right">Subtotal</th>
                        <th className="text-right">Tax</th>
                        <th className="text-right">Total</th>
                        <th>Consistency</th>
                        <th>Classification</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trace.supplier_invoices.map((si) => (
                        <tr key={si.invoice_id} className={si.skipped ? 'bg-red-50 dark:bg-red-950/20' : ''}>
                          <td className="font-mono">{si.invoice_number ?? si.invoice_id}</td>
                          <td className="text-right font-mono">{si.excluded ? '-' : formatCurrency(si.subtotal, trace.currency)}</td>
                          <td className="text-right font-mono">{si.excluded ? '-' : formatCurrency(si.tax_amount, trace.currency)}</td>
                          <td className="text-right font-mono">{si.excluded ? '-' : formatCurrency(si.total_amount, trace.currency)}</td>
                          <td>
                            {si.excluded ? (
                              <Badge variant="outline" className="text-[10px] text-amber-600">{si.exclusion_reason}</Badge>
                            ) : (
                              <Badge variant="outline" className={cn('text-[10px]', si.consistency_check === 'pass' ? 'text-green-600' : 'text-red-600')}>
                                {si.consistency_check}
                              </Badge>
                            )}
                          </td>
                          <td>
                            <Badge variant="outline" className={cn('text-[10px]',
                              si.classification === 'resolved' ? 'text-green-600' :
                              si.classification === 'skipped' ? 'text-red-600' : 'text-muted-foreground'
                            )}>
                              {si.classification ?? (si.excluded ? 'excluded' : '')}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Input VAT allocation result */}
            {inputVat && (
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Total Input VAT (booking)</p>
                  <p className="font-mono">{formatCurrency(inputVat.total_input_vat, trace.currency)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Input VAT (this invoice)</p>
                  <p className="font-mono">{inputVat.alloc_for_invoice != null ? formatCurrency(inputVat.alloc_for_invoice, trace.currency) : '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Input VAT Status</p>
                  <Badge variant="outline" className={cn('text-xs',
                    inputVat.status === 'resolved' ? 'text-green-600' :
                    inputVat.status === 'resolved_zero' ? 'text-muted-foreground' : 'text-amber-600'
                  )}>
                    {inputVat.status}
                  </Badge>
                </div>
              </div>
            )}
          </div>
        )}

        {trace?.error && (
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 text-sm text-red-700 dark:text-red-400">
            {trace.error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Supplier Cost Trace Card ─────────────────────────────────────────

function SupplierCostTraceCard() {
  const [bookingInput, setBookingInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [trace, setTrace] = useState(null);

  const runTrace = async () => {
    const bk = bookingInput.trim();
    if (!bk) { toast.error('Enter a booking ID'); return; }
    setLoading(true);
    setTrace(null);
    try {
      const { data } = await brosTaxReservesAPI.getSupplierCostTrace(bk);
      setTrace(data);
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success('Supplier cost trace loaded');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const comp = trace?.composition;
  const cross = trace?.cross_booking;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Supplier Cost Trace</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Trace the provenance and composition of supplier_total for a booking. Shows linked supplier invoices, the delta vs supplier_total, and cross-booking supplier invoices.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Booking ID</p>
            <Input
              value={bookingInput}
              onChange={(e) => setBookingInput(e.target.value)}
              placeholder="e.g. BK-ENQ-180235-050"
              className="w-56 h-8 text-sm font-mono"
              onKeyDown={(e) => { if (e.key === 'Enter') runTrace(); }}
            />
          </div>
          <Button size="sm" onClick={runTrace} disabled={loading} data-testid="run-supplier-trace-btn">
            {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Search className="h-3 w-3 mr-1" />}
            Run
          </Button>
        </div>

        {trace && !trace.error && (
          <div className="space-y-4">
            {/* Booking summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Booking</p>
                <p className="font-mono font-medium text-xs">{trace.booking}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Supplier Total</p>
                <p className="font-mono font-semibold" data-testid="sct-supplier-total">
                  {trace.supplier_total != null ? formatCurrency(trace.supplier_total, trace.currency) : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Property</p>
                <p className="text-xs">{trace.property_name ?? '-'} ({trace.property_country ?? '-'})</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Period</p>
                <p className="text-xs font-mono">{trace.check_in ?? '?'} to {trace.check_out ?? '?'}</p>
              </div>
            </div>

            {/* Provenance */}
            {trace.provenance && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Provenance
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                  <div>
                    <p className="text-muted-foreground">Source</p>
                    <p className="font-mono">{trace.provenance.source_field} via {trace.provenance.ingestion_function}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Last Synced</p>
                    <p className="font-mono">{trace.provenance.synced_at ?? '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Ingestion Status</p>
                    <Badge variant="outline" className={cn('text-[10px]',
                      trace.provenance.ingestion_status === 'ok' ? 'text-green-600' :
                      trace.provenance.ingestion_status === 'quarantined' ? 'text-red-600' : 'text-muted-foreground'
                    )}>
                      {trace.provenance.ingestion_status ?? '-'}
                    </Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2 italic">{trace.provenance.note}</p>
              </div>
            )}

            {/* Composition */}
            {comp && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Composition ({comp.invoice_count} supplier invoices)
                </h4>

                {/* Delta highlight */}
                <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Supplier Total (booking)</p>
                    <p className="font-mono">{formatCurrency(comp.supplier_total, trace.currency)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Sum of Supplier Invoices</p>
                    <p className="font-mono">{formatCurrency(comp.invoiced_total, trace.currency)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Delta</p>
                    <p className={cn('font-mono font-semibold', comp.delta_nonzero ? 'text-red-600' : 'text-green-600')}
                       data-testid="sct-delta">
                      {formatCurrency(comp.delta, trace.currency)}
                    </p>
                  </div>
                </div>

                {/* Supplier invoices table */}
                {comp.supplier_invoices?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="data-table text-xs">
                      <thead>
                        <tr>
                          <th>Invoice</th>
                          <th>Supplier</th>
                          <th className="text-right">Subtotal</th>
                          <th className="text-right">Tax</th>
                          <th className="text-right">Total</th>
                          <th>Consistency</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comp.supplier_invoices.map((si) => (
                          <tr key={si.invoice_id} className={si.excluded ? 'bg-amber-50 dark:bg-amber-950/20' : ''}>
                            <td className="font-mono">{si.invoice_number ?? si.invoice_id}</td>
                            <td className="text-xs">{si.supplier_name ?? '-'}</td>
                            <td className="text-right font-mono">{si.excluded ? '-' : formatCurrency(si.subtotal, trace.currency)}</td>
                            <td className="text-right font-mono">{si.excluded ? '-' : formatCurrency(si.tax_amount, trace.currency)}</td>
                            <td className="text-right font-mono">{si.excluded ? '-' : formatCurrency(si.total_amount, trace.currency)}</td>
                            <td>
                              {si.excluded ? (
                                <Badge variant="outline" className="text-[10px] text-amber-600">{si.exclusion_reason}</Badge>
                              ) : (
                                <Badge variant="outline" className={cn('text-[10px]', si.consistency_check === 'pass' ? 'text-green-600' : 'text-red-600')}>
                                  {si.consistency_check}
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {comp.invoice_count === 0 && (
                  <p className="text-xs text-muted-foreground italic">No supplier invoices (INV-S-*) linked to this booking.</p>
                )}
              </div>
            )}

            {/* Cross-booking check */}
            {cross && (
              <div>
                <h4 className={cn('text-xs font-semibold uppercase tracking-wider mb-2',
                  cross.match_count > 0 ? 'text-amber-600' : 'text-muted-foreground')}>
                  Cross-Booking Check ({cross.match_count} matches)
                </h4>
                {cross.supplier_names_checked?.length > 0 && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Checked suppliers: {cross.supplier_names_checked.join(', ')}
                  </p>
                )}
                {cross.matches?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="data-table text-xs">
                      <thead>
                        <tr>
                          <th>Invoice</th>
                          <th>Supplier</th>
                          <th className="text-right">Total</th>
                          <th>Linked Booking</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cross.matches.map((ci) => (
                          <tr key={ci.invoice_id}>
                            <td className="font-mono">{ci.invoice_number ?? ci.invoice_id}</td>
                            <td className="text-xs">{ci.supplier_name ?? '-'}</td>
                            <td className="text-right font-mono">{formatCurrency(ci.total_amount, ci.currency ?? trace.currency)}</td>
                            <td className="font-mono text-xs">{ci.booking_id}</td>
                            <td>
                              <Badge variant="outline" className="text-[10px]">{ci.ingestion_status ?? '-'}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No supplier invoices from the same suppliers found on other bookings.</p>
                )}
              </div>
            )}
          </div>
        )}

        {trace?.error && (
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 text-sm text-red-700 dark:text-red-400">
            {trace.error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Country Override Config Card ─────────────────────────────────────

function CountryOverrideCard({ overrides, onSave, saving }) {
  const [editing, setEditing] = useState(false);
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    if (overrides?.overrides) {
      setEntries(Object.entries(overrides.overrides).map(([client, country]) => ({ client, country })));
    }
  }, [overrides]);

  const handleAdd = () => {
    setEntries([...entries, { client: '', country: '' }]);
  };

  const handleRemove = (idx) => {
    setEntries(entries.filter((_, i) => i !== idx));
  };

  const handleChange = (idx, field, value) => {
    const next = [...entries];
    next[idx] = { ...next[idx], [field]: value };
    setEntries(next);
  };

  const handleSave = async () => {
    const map = {};
    for (const { client, country } of entries) {
      const c = client.trim();
      const co = country.trim();
      if (c && co) map[c] = co;
    }
    await onSave(map);
    setEditing(false);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Country Overrides (Client)</CardTitle>
          </div>
          {!editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Edit3 className="h-3 w-3 mr-1" /> Edit
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Override country attribution by client name. Applies when booking has no property_country.
        </p>
      </CardHeader>
      <CardContent>
        {entries.length === 0 && !editing && (
          <p className="text-sm text-muted-foreground">No overrides configured.</p>
        )}
        {entries.length > 0 && (
          <div className="space-y-2">
            {entries.map((entry, idx) => (
              <div key={idx} className="flex items-center gap-2">
                {editing ? (
                  <>
                    <Input
                      value={entry.client}
                      onChange={(e) => handleChange(idx, 'client', e.target.value)}
                      placeholder="Client name (exact match)"
                      className="flex-1 h-8 text-sm"
                    />
                    <Input
                      value={entry.country}
                      onChange={(e) => handleChange(idx, 'country', e.target.value)}
                      placeholder="Country code (e.g. UK, DE)"
                      className="w-24 h-8 text-sm"
                    />
                    <Button size="sm" variant="ghost" onClick={() => handleRemove(idx)} className="text-red-500 h-8 px-2">
                      x
                    </Button>
                  </>
                ) : (
                  <div className="flex items-center gap-3 text-sm">
                    <span className="font-mono">{entry.client}</span>
                    <span className="text-muted-foreground">&rarr;</span>
                    <Badge variant="outline">{entry.country}</Badge>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {editing && (
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={handleAdd}>
              + Add Rule
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => {
              if (overrides?.overrides) {
                setEntries(Object.entries(overrides.overrides).map(([client, country]) => ({ client, country })));
              }
              setEditing(false);
            }}>
              Cancel
            </Button>
          </div>
        )}
        {overrides?.updated_at && (
          <p className="text-xs text-muted-foreground mt-3">
            Last updated: {new Date(overrides.updated_at).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Unattributed Payments Card ───────────────────────────────────────

function UnattributedPaymentsCard() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await brosTaxReservesAPI.getUnattributedPayments();
      setData(res);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Unattributed Payments</CardTitle>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Load
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Payments still in "Unassigned" after property_country and client overrides. Grouped by client.
        </p>
      </CardHeader>
      <CardContent>
        {data && (
          <div className="space-y-2">
            {data.total_count === 0 ? (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="h-4 w-4" /> All payments attributed
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  {data.total_count} payment{data.total_count !== 1 ? 's' : ''} still unattributed,
                  total reserve: {Object.entries(data.total_reserve_by_currency ?? {}).map(([c, v]) => `${formatCurrency(v, c)}`).join(', ')}
                </p>
                <div className="overflow-x-auto">
                  <table className="data-table text-xs">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th className="text-right">Count</th>
                        <th className="text-right">Total VAT</th>
                        <th className="text-right">Total Corp Tax</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.by_client ?? []).map((row) => (
                        <tr key={row.client}>
                          <td className="font-mono">{row.client || <span className="text-muted-foreground italic">empty</span>}</td>
                          <td className="text-right">{row.count}</td>
                          <td className="text-right font-mono">{formatCurrency(row.vat_reserve, 'GBP')}</td>
                          <td className="text-right font-mono">{formatCurrency(row.corp_tax_reserve, 'GBP')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
        {!data && !loading && (
          <p className="text-sm text-muted-foreground">Click Load to see unattributed payments.</p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Partially-Invoiced Bookings Card ─────────────────────────────────

function PartiallyInvoicedCard() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [sortKey, setSortKey] = useState('gap');
  const [sortAsc, setSortAsc] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await brosTaxReservesAPI.getPartiallyInvoiced();
      setData(res);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(key === 'booking_id');
    }
  };

  const copyBookingId = (id) => {
    navigator.clipboard.writeText(id);
    toast.success(`Copied ${id}`);
  };

  const sortedBookings = (() => {
    const rows = [...(data?.bookings ?? [])];
    const numKeys = ['supplier_total', 'client_total', 'invoiced_total_net', 'gap', 'deposit_excluded', 'invoice_count'];
    rows.sort((a, b) => {
      let av, bv;
      if (sortKey === 'gap') {
        av = Math.abs(Number(a.gap ?? 0));
        bv = Math.abs(Number(b.gap ?? 0));
      } else if (numKeys.includes(sortKey)) {
        av = Number(a[sortKey] ?? 0);
        bv = Number(b[sortKey] ?? 0);
      } else {
        av = String(a[sortKey] ?? '');
        bv = String(b[sortKey] ?? '');
        return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortAsc ? av - bv : bv - av;
    });
    return rows;
  })();

  const SortHeader = ({ field, label, align }) => (
    <th
      className={cn('cursor-pointer select-none hover:text-foreground', align === 'right' && 'text-right')}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        {sortKey === field && (sortAsc
          ? <ArrowUp className="h-3 w-3" />
          : <ArrowDown className="h-3 w-3" />
        )}
      </span>
    </th>
  );

  const summary = data?.summary;
  const fieldReport = data?.field_report;
  const recs = data?.denominator_recommendations;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Partially-Invoiced Bookings</CardTitle>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Load
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Bookings where invoiced client value differs from supplier_total. Gap drives unallocated supplier cost under client_total denominator.
        </p>
      </CardHeader>
      <CardContent>
        {data && (
          <div className="space-y-4">
            {data.flagged_count === 0 ? (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="h-4 w-4" /> All bookings fully invoiced
              </div>
            ) : (
              <>
                {/* Summary strip */}
                <div className="flex flex-wrap gap-4 items-center text-sm">
                  <Badge variant="outline" className="text-xs">
                    {data.flagged_count} flagged
                  </Badge>
                  {summary && Object.entries(summary.total_gap_by_currency ?? {}).map(([cur, total]) => (
                    <span key={cur} className="text-muted-foreground text-xs">
                      {cur}: <span className="font-mono font-medium">{formatCurrency(total, cur)}</span> net gap
                    </span>
                  ))}
                  {summary?.worst_offender && (
                    <span className="text-xs text-amber-600">
                      Worst: <span className="font-mono">{summary.worst_offender}</span>{' '}
                      ({formatCurrency(summary.worst_gap, summary.worst_currency ?? 'GBP')})
                    </span>
                  )}
                </div>

                {/* Sortable table, top 20 by absolute gap */}
                <div className="overflow-x-auto">
                  <table className="data-table text-xs">
                    <thead>
                      <tr>
                        <SortHeader field="booking_id" label="Booking" />
                        <SortHeader field="currency" label="Ccy" />
                        <SortHeader field="supplier_total" label="Supplier" align="right" />
                        <SortHeader field="client_total" label="Client" align="right" />
                        <SortHeader field="invoiced_total_net" label="Invoiced" align="right" />
                        <th
                          className={cn('cursor-pointer select-none hover:text-foreground text-right')}
                          onClick={() => handleSort('gap')}
                          title="Invoiced minus client_total (or supplier_total when client_total is absent). Positive = over-invoiced."
                        >
                          <span className="inline-flex items-center gap-0.5">
                            Gap
                            {sortKey === 'gap' && (sortAsc
                              ? <ArrowUp className="h-3 w-3" />
                              : <ArrowDown className="h-3 w-3" />
                            )}
                          </span>
                        </th>
                        <SortHeader field="invoice_count" label="#Inv" align="right" />
                        <th>Source</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedBookings.map((row) => (
                        <tr
                          key={row.booking_id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => copyBookingId(row.booking_id)}
                          title="Click to copy booking ID"
                        >
                          <td className="font-mono flex items-center gap-1">
                            {row.booking_id}
                            <Copy className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
                          </td>
                          <td>{row.currency}</td>
                          <td className="text-right font-mono">{formatCurrency(row.supplier_total, row.currency)}</td>
                          <td className="text-right font-mono">
                            {row.client_total ? formatCurrency(row.client_total, row.currency) : <span className="text-muted-foreground">-</span>}
                          </td>
                          <td className="text-right font-mono">{formatCurrency(row.invoiced_total_net, row.currency)}</td>
                          <td className={cn("text-right font-mono", row.invoice_status === 'over_invoiced' ? "text-amber-600" : row.invoice_status === 'under_invoiced' ? "text-blue-600" : "text-muted-foreground")}>
                            {formatCurrency(row.gap, row.currency)}
                          </td>
                          <td className="text-right">{row.invoice_count}</td>
                          <td>
                            {row.denominator_source === 'client_total' ? (
                              <Badge variant="outline" className="text-xs text-green-600 border-green-300">client_total</Badge>
                            ) : row.denominator_source === 'invoice_sum_capped' ? (
                              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">capped</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs text-muted-foreground">fallback</Badge>
                            )}
                          </td>
                          <td>
                            {row.invoice_status === 'over_invoiced' && row.stale_totals ? (
                              <Badge variant="outline" className="text-xs text-red-600 border-red-300">Over (stale?)</Badge>
                            ) : row.invoice_status === 'over_invoiced' ? (
                              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Over</Badge>
                            ) : row.invoice_status === 'under_invoiced' ? (
                              <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">Under</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs text-muted-foreground">No client total</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Diagnostics detail (collapsed) */}
            {(fieldReport ?? recs) && (
              <div>
                <button
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setDiagOpen(!diagOpen)}
                >
                  {diagOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  Diagnostics detail
                </button>
                {diagOpen && (
                  <div className="space-y-3 mt-2">
                    {fieldReport && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          Booking field population ({fieldReport.total_bookings} bookings)
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          {Object.entries(fieldReport.fields ?? {}).map(([field, info]) => (
                            <div key={field} className="bg-muted/50 rounded p-1.5">
                              <span className="font-mono">{field}</span>
                              <span className="text-muted-foreground ml-1">
                                {info.populated}/{fieldReport.total_bookings} ({info.pct}%)
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {recs && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Denominator options</p>
                        <div className="space-y-2">
                          {recs.map((rec) => (
                            <div key={rec.option} className="bg-muted/50 rounded p-2 text-xs">
                              <p className="font-mono font-medium">{rec.option}</p>
                              <p className="text-muted-foreground mt-0.5">{rec.description}</p>
                              <p className="text-amber-600 mt-0.5">Risk: {rec.risk}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {!data && !loading && (
          <p className="text-sm text-muted-foreground">Click Load to scan bookings for invoicing gaps.</p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Page ────────────────────────────────────────────────────────

// Inner content (no DashboardLayout) for embedding under the entity tax route.
export function BROSTaxReservesContent() {
  const [rules, setRules] = useState(null);
  const [balances, setBalances] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [lastWeeklyRun, setLastWeeklyRun] = useState(null);
  const [countryStats, setCountryStats] = useState(null);
  const [paymentsData, setPaymentsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accruing, setAccruing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [weeklyRunning, setWeeklyRunning] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('payments'); // 'payments' | 'ledger'
  const [activeTab, setActiveTab] = useState(
    () => new URLSearchParams(window.location.search).get('tab') || 'overview',
  );
  const [countryOverrides, setCountryOverrides] = useState(null);
  const [overrideSaving, setOverrideSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [rulesRes, balancesRes, ledgerRes, weeklyRes, paymentsRes, countryRes, overridesRes] = await Promise.all([
        brosTaxReservesAPI.getRules(),
        brosTaxReservesAPI.getBalances(),
        brosTaxReservesAPI.getLedger(),
        brosTaxReservesAPI.getLastWeeklyRun(),
        brosTaxReservesAPI.getPayments({ status: statusFilter !== 'all' ? statusFilter : undefined }),
        brosTaxReservesAPI.getCountryStats(),
        brosTaxReservesAPI.getCountryOverrides(),
      ]);
      setRules(rulesRes.data);
      setBalances(balancesRes.data);
      setLedger(ledgerRes.data);
      setLastWeeklyRun(weeklyRes.data?.last_run ?? null);
      setPaymentsData(paymentsRes.data);
      setCountryStats(countryRes.data);
      setCountryOverrides(overridesRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  const fetchDiagnostics = useCallback(async () => {
    setDiagLoading(true);
    try {
      const res = await brosTaxReservesAPI.getDiagnostics();
      setDiagnostics(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDiagLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    fetchDiagnostics();
  }, [fetchAll, fetchDiagnostics]);

  const handleAccrue = async () => {
    setAccruing(true);
    try {
      const res = await brosTaxReservesAPI.runAccrual();
      const d = res.data;
      toast.success(
        `Accrual complete: ${d.created} created, ${d.adjusted} adjusted, ${d.skipped} skipped` +
        (d.errors?.length > 0 ? `, ${d.errors.length} errors` : '')
      );
      await fetchAll();
      await fetchDiagnostics();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setAccruing(false);
    }
  };

  const handleSaveRules = async (params) => {
    setSaving(true);
    try {
      await brosTaxReservesAPI.updateRules(params);
      toast.success('Reserve rules updated');
      await fetchAll();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCountryOverrides = async (overrideMap) => {
    setOverrideSaving(true);
    try {
      await brosTaxReservesAPI.updateCountryOverrides({ overrides: overrideMap });
      toast.success('Country overrides updated');
      await fetchAll();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setOverrideSaving(false);
    }
  };

  const handleRunWeekly = async () => {
    setWeeklyRunning(true);
    try {
      const res = await brosTaxReservesAPI.runWeekly();
      const d = res.data;
      setLastWeeklyRun(d);
      toast.success('Weekly budget proposal generated');
      await fetchAll();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setWeeklyRunning(false);
    }
  };

  const handleToggleStatus = async (paymentId, newStatus) => {
    try {
      await brosTaxReservesAPI.updatePaymentStatus(paymentId, newStatus);
      await fetchAll();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleBulkStatus = async (paymentIds, status) => {
    try {
      await brosTaxReservesAPI.bulkUpdateStatus({
        payment_ids: paymentIds,
        status,
      });
      toast.success(`${status === 'transferred' ? 'Marked transferred' : 'Marked budgeted'}`);
      await fetchAll();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
      <div className="flex flex-col gap-6" data-testid="bros-tax-reserves-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Tax Reserves</h1>
            <p className="text-muted-foreground mt-1">
              BR OS payment-level VAT and corporation tax reserves
            </p>
          </div>
          <Button onClick={handleAccrue} disabled={accruing} data-testid="run-accrual-btn">
            {accruing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Run Accrual
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="cash">Cash waterfall</TabsTrigger>
              <TabsTrigger value="vat">VAT by month</TabsTrigger>
              <TabsTrigger value="controls">Controls</TabsTrigger>
              <TabsTrigger value="payments">Supplier payments</TabsTrigger>
              <TabsTrigger value="debts">Tax debts</TabsTrigger>
            </TabsList>

            <TabsContent value="debts">
              {activeTab === 'debts' && <div className="pt-2"><PayrollTaxDebtsPanel entityId="br_ltd" show="debts" /></div>}
            </TabsContent>

            <TabsContent value="payments">
              {activeTab === 'payments' && <SupplierPayments />}
            </TabsContent>

            <TabsContent value="overview">
              <div className="space-y-6 pt-2">
                {/* Fan, 2026-10-27: "do it for Tax Reserves as well under BR
                    OS scope" - the Property Portfolio layout first (KPI
                    cards, monthly chart and table, by country); the earlier
                    sections stay below under "More detail". */}
                <BROSTaxOverview />

                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider pt-4">
                  More detail
                </h2>

                {/* Fan, 2026-08-30: the take-home / working-capital split was
                    only visible inside the waterfall tab, so it belongs here on
                    the landing tab too. */}
                <BROSMoneySplitSummary onSeeDetail={() => setActiveTab('cash')} />
                <BROSReserveTransfers />

                {/* Fan, 2026-09-02: "STILL no profitability line". */}
                <ProfitabilitySummary
                  profitability={balances?.profitability}
                  note={balances?.profitability_note}
                />

                {/* Fan, 2026-09-03: "no visibility on a month by month basis
                    and no visibility on profitability ... I cannot see which
                    payments you took into account". Shipped first on the group
                    Tax Reserves page, which is not the page Fan uses - it
                    belongs here, on the BR OS overview tab. */}
                <TaxReserveCountryMonths />

                {/* Fan, 2026-09-03: "The German master leases are also COGS!
                    ... noting that in BR OS as well. So they know we paid" */}
                <BROSMasterLeases />

                {/* Per-country stat cards */}
                {countryStats?.countries?.length > 0 && (
                  <div>
                    <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                      Reserve by Country
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {countryStats.countries.map((stat) => (
                        <CountryStatCard key={stat.country} stat={stat} />
                      ))}
                    </div>
                  </div>
                )}

                {/* View toggle: Payments vs Raw Ledger */}
                <div className="flex items-center gap-2">
                  <Button
                    variant={viewMode === 'payments' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('payments')}
                  >
                    <Table2 className="h-3 w-3 mr-1" /> Per-Payment
                  </Button>
                  <Button
                    variant={viewMode === 'ledger' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('ledger')}
                  >
                    <List className="h-3 w-3 mr-1" /> Raw Ledger
                  </Button>
                </div>

                {/* Table view */}
                {viewMode === 'payments' ? (
                  <PaymentsTable
                    payments={paymentsData?.payments ?? []}
                    totalsByCurrency={paymentsData?.totals_by_currency}
                    unresolvedSupplierCount={paymentsData?.unresolved_supplier_count ?? 0}
                    statusFilter={statusFilter}
                    onStatusFilterChange={setStatusFilter}
                    onToggleStatus={handleToggleStatus}
                    onBulkStatus={handleBulkStatus}
                    onRefresh={fetchAll}
                  />
                ) : (
                  <LedgerTable entries={ledger?.entries ?? []} />
                )}
              </div>
            </TabsContent>

            <TabsContent value="cash">
              <div className="space-y-6 pt-2">
                <BROSCashWaterfall />
              </div>
            </TabsContent>

            <TabsContent value="vat">
              <div className="space-y-6 pt-2">
                <BROSVatMonths />
              </div>
            </TabsContent>

            <TabsContent value="controls">
              <div className="space-y-6 pt-2">
                {/* Recompute Reserves */}
                <RecomputeReservesCard onRefresh={() => { fetchAll(); fetchDiagnostics(); }} />

                {/* Diagnostics */}
                <DiagnosticsPanel
                  diagnostics={diagnostics}
                  isLoading={diagLoading}
                  onRefresh={fetchDiagnostics}
                />

                {/* Rules config */}
                <RulesConfigCard rules={rules} onSave={handleSaveRules} saving={saving} />

                {/* Weekly budget proposal */}
                <WeeklyProposalCard
                  lastRun={lastWeeklyRun}
                  onRunWeekly={handleRunWeekly}
                  running={weeklyRunning}
                />

                {/* Allocation Trace */}
                <AllocationTraceCard />

                {/* Supplier Cost Trace */}
                <SupplierCostTraceCard />

                {/* Country overrides */}
                <CountryOverrideCard
                  overrides={countryOverrides}
                  onSave={handleSaveCountryOverrides}
                  saving={overrideSaving}
                />

                {/* Unattributed payments diagnostic */}
                <UnattributedPaymentsCard />

                {/* Partially-invoiced bookings diagnostic */}
                <PartiallyInvoicedCard />

                {/* Sweep + Draw actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ActionCard
                    title="Record Tax Bill Payment (Sweep)"
                    icon={ArrowDownCircle}
                    iconColor="text-blue-600"
                    bucketOptions={[
                      { value: 'vat_reserve', label: 'VAT Reserve' },
                      { value: 'corp_tax_reserve', label: 'Corp Tax Reserve' },
                    ]}
                    actionType="sweep"
                    onRefresh={() => { fetchAll(); fetchDiagnostics(); }}
                  />
                  <ActionCard
                    title="Record Profit Draw"
                    icon={ArrowUpCircle}
                    iconColor="text-green-600"
                    bucketOptions={[
                      { value: 'operating_cash', label: 'Operating Cash' },
                    ]}
                    actionType="draw"
                    onRefresh={() => { fetchAll(); fetchDiagnostics(); }}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
  );
}

export function BROSTaxReservesPage() {
  return (
    <DashboardLayout>
      <BROSTaxReservesContent />
    </DashboardLayout>
  );
}
